import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Newspaper, Search, Clock, Eye, User, Tag, ArrowRight, Lock } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const PublicBlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  useEffect(() => {
    axios.get(`${API}/blog/public`).then(r => setPosts(r.data)).catch(() => {
      axios.get(`${API}/blog?published_only=true`).then(r => setPosts(r.data)).catch(() => {});
    }).finally(() => setLoading(false));
  }, []);

  const allTags = [...new Set(posts.flatMap(p => p.tags || []))].sort();

  const filtered = posts.filter(p => {
    if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !(p.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedTag && !(p.tags || []).includes(selectedTag)) return false;
    return true;
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <SEO title="Blog" description="Blog de la SFOALC. Actualites, articles et informations sur la contactologie et l'ophtalmologie." />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white" style={{ transform: 'translate(30%, -30%)' }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-semibold rounded-full mb-6">
            <Newspaper size={14} /> SFOALC — Actualités
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="blog-title">
            Blog & Actualités
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Toute l'actualité de la contactologie médicale et de la SFOALC
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f9fafb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10">

        {/* Search + tags */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-lg">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Rechercher un article..." className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30" data-testid="search-input" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="tags-filter">
            {allTags.map(tag => (
              <button key={tag} onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTag === tag ? 'bg-[#35AFE4] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>{tag}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl"><Newspaper size={64} className="mx-auto mb-5 text-gray-300" /><h3 className="text-xl font-bold text-gray-700 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Aucun article trouvé</h3></div>
        ) : (
          <>
            {/* Featured post */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="block mb-10" data-testid="featured-post">
                <div className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative aspect-[16/9] lg:aspect-auto overflow-hidden bg-gray-100">
                      {featured.thumbnail ? (
                        <img src={featured.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center min-h-[250px]"><Newspaper size={64} className="text-white/30" /></div>
                      )}
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col justify-center">
                      {featured.category && <span className="text-[#35AFE4] text-xs font-bold uppercase tracking-wider mb-3">{featured.category}</span>}
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>{featured.title}</h2>
                      <p className="text-gray-500 text-sm line-clamp-3 mb-4">{featured.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        {featured.author_name && <span className="flex items-center gap-1"><User size={12} /> {featured.author_name}</span>}
                        {featured.created_at && <span className="flex items-center gap-1"><Clock size={12} /> {new Date(featured.created_at).toLocaleDateString('fr-FR')}</span>}
                        {featured.views > 0 && <span className="flex items-center gap-1"><Eye size={12} /> {featured.views}</span>}
                      </div>
                      <span className="inline-flex items-center gap-1.5 mt-4 text-[#35AFE4] text-sm font-bold">Lire la suite <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Rest of posts */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {rest.map((post, i) => (
                  <Link to={`/blog/${post.slug}`} key={post.id} className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all" data-testid={`blog-card-${i}`}>
                    <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
                      {post.thumbnail ? (
                        <img src={post.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center"><Newspaper size={40} className="text-white/30" /></div>
                      )}
                      {post.category && <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-800 text-[10px] font-bold px-2.5 py-1 rounded-full">{post.category}</span>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>{post.title}</h3>
                      <p className="text-gray-500 text-xs line-clamp-2 mb-3">{post.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {post.author_name && <span className="flex items-center gap-1"><User size={12} /> {post.author_name}</span>}
                        {post.created_at && <span className="flex items-center gap-1"><Clock size={12} /> {new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PublicBlogPage;
